import { useState, useEffect, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { InfoCircle } from "untitledui-js/react";
import { Typography } from "@pondigitalsolutions/pon-component-library";

import { Layout } from "@components/Layout";
import { Loader } from "@components/Loader";
import { useDebounce } from "@hooks/useDebounce";
import { useStoryblokProxy } from "@hooks/useStoryblokProxy";
import { languageDutch, languageEnglish } from "../i18n";
import { PeopleFinderCard } from "@components/PeopleFinderCard";
import { SearchBar } from "@components/SearchBar";
import {
  PeopleFinderPerson,
  usePeopleFinder,
} from "@hooks/peopleFinder/usePeopleFinder";
import { Alert } from "@components/Alert";
import {
  MINIMAL_SEARCH_CHARACTERS,
  PeopleFinder,
  MINIMAL_CONFIDENCE_LEVEL_PEOPLE_FINDER,
  MAXIMAL_RESULTS_PEOPLE_FINDER,
} from "../constants";
import type { ISbStoryData } from "@storyblok/react";
import { useCurrentLang } from "@hooks/useCurrentLang";
import { PeopleFinderDetails } from "@components/PeopleFinderDetails";

interface PeopleFinderPageContent {
  [key: string]: string | undefined;
}

interface PeopleFinderPageStory extends ISbStoryData {
  content: PeopleFinderPageContent;
}

export const PeopleFinderPage = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const currentLang = useCurrentLang();

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [inputValue, setInputValue] = useState<string>("");
  const [selectedPerson, setSelectedPerson] =
    useState<PeopleFinderPerson | null>(null);

  const debouncedSearchTerm = useDebounce(inputValue, 500);

  // Fetch People Finder page content from Storyblok
  const { data: storyblokData, isLoading: isStoryblokLoading } =
    useStoryblokProxy({
      slug: PeopleFinder,
      language: lang === languageEnglish ? languageEnglish : undefined,
    });

  const story = storyblokData?.story as PeopleFinderPageStory;

  useEffect(() => {
    setSearchTerm(debouncedSearchTerm.trim());
  }, [debouncedSearchTerm]);

  const { data, error, isLoading } = usePeopleFinder({ searchTerm });

  /**
   * Derive filtered people synchronously from API response data.
   *
   * Using useMemo instead of useState + useEffect prevents a flash of the
   * "not found" alert. With useEffect, there's a render gap where `data` exists
   * but `people` is still empty from the previous state, briefly showing the alert.
   * useMemo ensures `people` is computed in the same render cycle as `data` updates.
   */
  const people = useMemo(() => {
    if (!data?.response) return [];
    return data.response.filter(
      (person) => person.confidence >= MINIMAL_CONFIDENCE_LEVEL_PEOPLE_FINDER
    );
  }, [data]);

  // Get the page title from Storyblok or use a default title
  const pageTitle = story?.name || t("PeopleFinder.title");

  // Derived conditions for cleaner JSX
  const hasSearched = searchTerm && !isLoading;
  const noResultsFound =
    hasSearched &&
    (error?.response?.status === 404 || (data && people.length === 0));
  const hasActualError = hasSearched && error && error.response?.status !== 404;

  const resetSearch = () => {
    setSearchTerm("");
    setInputValue("");
  };

  const alertTextClassName = "text-tertiary-950 dark:text-tertiary-100";

  const noResultsText = (
    <Trans
      i18nKey="PeopleFinder.noResults"
      components={{
        1: (
          <button
            className="cursor-pointer font-semibold underline"
            onClick={resetSearch}
          />
        ),
      }}
    />
  );

  return (
    <Layout
      translatedSlug={`${currentLang === languageDutch ? `/${languageEnglish}` : ""}/${PeopleFinder}`}
    >
      {isStoryblokLoading ? (
        <Loader />
      ) : (
        <>
          <title>{pageTitle}</title>
          <div className="bg-secondary-900 py-14 md:px-4 2xl:px-0 dark:bg-transparent">
            <div className="mx-auto max-w-360">
              <Typography
                as="h1"
                size="3xl"
                className="text-center leading-[120%] font-semibold text-white lg:text-4xl"
              >
                {pageTitle}
              </Typography>
            </div>
          </div>

          <div className="mx-auto my-8 max-w-230 px-4 lg:my-14 2xl:px-0">
            <div className="mb-10 flex flex-col items-center gap-2 lg:mb-2">
              <SearchBar
                placeholder={t("PeopleFinder.searchPlaceholder")}
                isLoading={isLoading}
                isDisabled={
                  isLoading || inputValue.length < MINIMAL_SEARCH_CHARACTERS
                }
                onClick={(searchValue) => {
                  setSearchTerm(searchValue);
                }}
                onInputChange={(value) => {
                  setInputValue(value);
                }}
                searchValue={inputValue}
                hasFocus
              />
              <div className="mx-8 flex items-center justify-center gap-4">
                <InfoCircle className="h-3 w-3 shrink-0 stroke-secondary-700 dark:stroke-white" />
                <Typography
                  size="sm"
                  className="leading-[120%] font-normal text-tertiary-900 dark:text-tertiary-100"
                >
                  {t("PeopleFinder.searchInfo")}
                </Typography>
              </div>

              {hasActualError && (
                <Alert
                  labelText={t("AlertLabel.error")}
                  text={error.message}
                  variant="error"
                  className="mt-12 lg:mt-14"
                  textClassName={alertTextClassName}
                />
              )}

              {noResultsFound && (
                <Alert
                  data-testid="no-results-found-alert"
                  labelText={t("AlertLabel.whoops")}
                  text={noResultsText}
                  variant="warning"
                  className="mt-12 lg:mt-14"
                  textClassName={alertTextClassName}
                />
              )}
            </div>

            {people && people.length > 0 && (
              <>
                <div className="flex flex-col gap-2 lg:gap-1">
                  {people.map((person) => (
                    <PeopleFinderCard
                      key={person.opcoEmailAccount}
                      person={person}
                      searchTerm={searchTerm}
                      onClick={() => setSelectedPerson(person)}
                    />
                  ))}
                </div>

                <Typography
                  size="sm"
                  className="mt-2 text-center leading-[120%] font-normal text-tertiary-500 dark:text-tertiary-300"
                >
                  {t("PeopleFinder.peopleFound", { count: people.length })}
                </Typography>

                {people.length >= MAXIMAL_RESULTS_PEOPLE_FINDER && (
                  <div className="mt-16 flex justify-center lg:mt-12">
                    <Alert
                      labelText={t("AlertLabel.tip")}
                      text={t("PeopleFinder.tipText")}
                      textClassName={alertTextClassName}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          <PeopleFinderDetails
            isOpen={!!selectedPerson}
            person={selectedPerson}
            closeDetails={() => setSelectedPerson(null)}
          />
        </>
      )}
    </Layout>
  );
};
